/* eslint-disable react/prop-types */
/* eslint-disable max-len */
/* eslint-disable no-param-reassign */
import React, { useCallback, useEffect, useState } from 'react';

import PropTypes from 'prop-types';
import { ViewModelForm, useDataRefresh } from '@xengage/gw-portals-viewmodel-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { useTranslator } from '@jutro/locale';
import appConfig from 'app-config';
import classNames from 'classnames';
import '@xengage/gw-platform-translations';
import _ from 'lodash';

import messages from './WatercraftPopupComponent.messages';
import metadata from './WatercraftPopupComponent.metadata.json5';
import styles from './WatercraftPopupComponent.module.scss';

function WatercraftPopupComponent(props) {
    const translator = useTranslator();

    const {
        id,
        onValidate,
        showValidationErrors,
        watercraftVM,
        pupModel,
        onModelChange,
        onSave,
        getUnderlyingPolicies,
        createUnderlyingPolicy,
        showActionRow,
        onCancel
    } = props;

    const SAILBOATS = ['sailboat_with_auxillary_ml', 'sailboat_without_auxillary_ml'];
    const SAILBOAT_LENGTHS = ['lessthan26', 'greaterthanequalto26'];
    const UNP_WATERCRAFT_TYPES = ['sportfisher_ext', 'trawler_ext'];
    const HORSEPOWER_LABELS = [
        { code: 'lessthan50', name: "< 50" },
        { code: '51to300', name: "51 to 300" },
        { code: '301to500', name: "301 to 500" },
        { code: '501plus', name: "501 +" }
    ]
    const LENGTH_LABELS = [
        { code: 'lessthan26', name: "< 26'" },
        { code: 'greaterthanequalto26', name: ">= 26'" },
        { code: '26to29', name: "26' to < 30'" },
        { code: '30to39', name: "30' to < 40'" },
        { code: '40to49', name: "40' to < 50'" },
        { code: '50plus', name: "50' +" }
    ];

    const [showErrors, updateShowErrors] = useState(showValidationErrors);
    const underlyingPolicies = pupModel.underlyingPolicies.value;
    const exposureType = 'watercraft';
    const { refreshData } = useDataRefresh();
    const isGPA = appConfig.portalCode === 'GPA';
    const isAMP = appConfig.portalCode === 'AMP';

    if (_.isUndefined(watercraftVM.isExcluded.value)) {
        watercraftVM.isExcluded.value = false;
    }
    watercraftVM.value.isEditing = true;


    const mapAvailableValues = (values) => {
        return values.map((type) => {
            return {
                code: type.code,
                name: translator({
                    id: type.name,
                    defaultMessage: type.name
                })
            };
        });
    };

    const mapDropdownLabelValues = (values, labelsArray) => {
        return values.map((type) => {
            return {
                code: type.code,
                name: labelsArray.find(item => type.code === item.code).name
            };
        });
    };

    const getAvailableWatercraftTypes = () => {
        const availableValues = watercraftVM.watercraftType.aspects.availableValues || [];

        if (watercraftVM.isModernProduct.value) {
            return mapAvailableValues(availableValues);
        }

        const legacyValues = availableValues.filter(
            (type) => !UNP_WATERCRAFT_TYPES.includes(type.code)
        );
        
        return mapAvailableValues(legacyValues);
    };

    const getAvailableMoorStates = () => {
        return mapAvailableValues(watercraftVM.moorState.aspects.availableValues);
    };

    const getAvailableWatercraftLengths = () => {
        const watercraftType = _.get(watercraftVM, "watercraftType.value.code");
        if (SAILBOATS.includes(watercraftType)) {
            const values = watercraftVM.lengthinFeet.aspects.availableValues.filter(value => SAILBOAT_LENGTHS.includes(value.code));
            return mapDropdownLabelValues(values, LENGTH_LABELS)
        }
        const values = watercraftVM.lengthinFeet.aspects.availableValues.filter(value => value.code !== 'greaterthanequalto26');
        return mapDropdownLabelValues(values, LENGTH_LABELS);
    };

    const getHorsePowerAvailableValues = () => {
        const values = watercraftVM.horsePowerNP.aspects.availableValues;
        return mapDropdownLabelValues(values, HORSEPOWER_LABELS);
    };

    const { onValidate: setComponentValidation, isComponentValid } = useValidation(id);
    useEffect(() => {
        if (onValidate) {
            onValidate(isComponentValid, id);
        }
    }, [id, onValidate, isComponentValid]);

    useEffect(() => {
        updateShowErrors(showValidationErrors)
    }, [showValidationErrors]);

    const addNewUnderlyingPolicyIfNew = () => {
        if(createUnderlyingPolicy){
            Promise.resolve(createUnderlyingPolicy(exposureType)).then((model) => {
                let val;
                if(model){
                    val = model.ulPolicyFixID.value;
                }
                watercraftVM.ulPolicyFixID.value = val;
                onModelChange(watercraftVM);
                refreshData();
            })
        }
    };

    const isWatercraftTypeSailboatWithoutAux = () => {
        if (watercraftVM.watercraftType && watercraftVM.watercraftType.value && watercraftVM.watercraftType.value.code) {
            return watercraftVM.watercraftType.value.code === 'sailboat_without_auxillary_ml';
        }
        return true;
    };

    const syncExcludeWatercraftChange = (value) => {
        watercraftVM.isExcluded.value = value;
        onModelChange(watercraftVM);
    };

    const syncUnderlyingPolicyChange = (value) => {
        watercraftVM.ulPolicyFixID.value = value;
        if (watercraftVM.ulPolicyFixID.value === 'new') {
            addNewUnderlyingPolicyIfNew();
        }
        onModelChange(watercraftVM);
    };

    const onSaveDetails = useCallback(() => {
        if (!(watercraftVM.aspects.valid && watercraftVM.aspects.subtreeValid)) {
            updateShowErrors(true);
            return false;
        }
        if (onSave) {
            return onSave(watercraftVM);
        }
        return false;
    }, [watercraftVM, onSave]);

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            onValidate: setComponentValidation,
            onSaveDetails: onSaveDetails,
            onCancel: onCancel
        }
    };

    const overrides = {
        horsepowerMPHSection: {
            visible: !isWatercraftTypeSailboatWithoutAux()
        },
        watercraftNotExcludedSection: {
            visible: !(watercraftVM.isExcluded.value === true)
        },
        watercraftType: {
            availableValues: getAvailableWatercraftTypes(),
            value: watercraftVM.watercraftType.value
        },
        watercraftTypeTooltipComponent: {
            tooltipLabel: messages.WatercraftTypeTooltipLabel,
            tooltipContent: watercraftVM.isModernProduct.value ? messages.WatercraftTypeTooltipContentNP : messages.WatercraftTypeTooltipContent,
        },
        moorState: {
            availableValues: getAvailableMoorStates(),
            value: watercraftVM.moorState.value
        },
        excludeWatercraftRadioButton: {
            onValueChange: syncExcludeWatercraftChange,
            value: watercraftVM.isExcluded.value
        },
        underlyingPolicies: {
            onValueChange: syncUnderlyingPolicyChange,
            availableValues: getUnderlyingPolicies(),
            value: watercraftVM.ulPolicyFixID.value
        },
        actionRow: {
            className: isGPA ? 'row col-md-6 vertical-spacing-30' : 'row col-md-3 vertical-spacing-30',
            visible: showActionRow
        },
        saveButton: {
            content: isGPA ? translator(messages.GPAButtonLabel) : translator(messages.Continue)
        },
        watercraftTypeSelect: {
            className:  classNames('vertical-spacing-30', isAMP ? 'col-md-6' : 'col-md-4')
        },
        watercraftTypeTooltipContainer: {
            className: classNames('vertical-spacing-30', styles.tooltipContainerOverride, isAMP ? 'col-md-6' : 'col-md-5')
        },
        hinControl: {
            className: classNames('vertical-spacing-30', isAMP ? 'col-md-6' : 'col-md-4')
        },
        hINTooltipContainer: {
            className: classNames('vertical-spacing-30', styles.tooltipContainerOverride, isAMP ? 'col-md-6' : 'col-md-4')
        },
        lengthControl: {
            className: isAMP ? 'col-md-6' : 'col-md-4',
            visible: !watercraftVM.isModernProduct.value
        },
        horsePowerControl: {
            className: isAMP ? 'col-md-6' : 'col-md-4',
            visible: !watercraftVM.isModernProduct.value
        },
        lengthType: {
            availableValues: getAvailableWatercraftLengths()
        },
        lengthinFeetSelect: {
            className: isAMP ? 'col-md-6' : 'col-md-4',
            visible: watercraftVM.lengthinFeet.aspects.required
        },
        horsePowerType: {
            availableValues: getHorsePowerAvailableValues()
        },
        horsePowerSelect: {
            className: isAMP ? 'col-md-6' : 'col-md-4',
            visible: watercraftVM.horsePowerNP.aspects.required
        },
        maxMPHControl: {
            className: isAMP ? 'col-md-6' : 'col-md-4'
        },
        moorStateControl: {
            className: isAMP ? 'col-md-6' : 'col-md-4'
        },
        underlyingPoliciesRow: {
            className: isAMP ? 'col-md-6' : 'col-md-4'
        }
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            overrideProps={overrides}
            model={watercraftVM}
            onValidationChange={setComponentValidation}
            showErrors={showErrors}
            onModelChange={onModelChange}
            componentMap={resolvers.resolveComponentMap}
            callbackMap={resolvers.resolveCallbackMap}
            classNameMap={resolvers.resolveClassNameMap}
        />
    );
}

WatercraftPopupComponent.propTypes = {
    id: PropTypes.string,
    onValidate: PropTypes.func,
    showValidationErrors: PropTypes.bool,
    pupModel: PropTypes.shape({}).isRequired,
    watercraftVM: PropTypes.shape({}).isRequired,
    onModelChange: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    getUnderlyingPolicies: PropTypes.func.isRequired,
    createUnderlyingPolicy: PropTypes.func.isRequired,
    showActionRow: PropTypes.bool,
    onCancel: PropTypes.func.isRequired
};

WatercraftPopupComponent.defaultProps = {
    id: undefined,
    onValidate: undefined,
    showValidationErrors: undefined,
    showActionRow: true
};

export default WatercraftPopupComponent;
